import produce from 'immer';
import { asDateString, createCalendar, WeekdayKey } from '../../common';
import { getTimeGeo, Response, TimeGeo } from '../../services';
import { changed } from '../../utils';
import { BookingState } from '../state';
import { currentDateAvailable, currentDateChanged } from '../stateUtils';
import { LifecycleEvent, SideEffect, SideEffectGenerator, Store } from '../types';
import { bindActions } from 'redux-zero/utils';

export const CALENDAR_DAYS = 360;

export const getTimeGeoEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _setTimeGeoLoading: (state) =>
        produce(state, ({ ux }) => {
          ux.loading.getTimeGeo = true;
        }),
      _setTimeGeoData: (state, { msepoch, lat, long }) =>
        produce(state, ({ ux }: BookingState) => {
          const currentDate = asDateString(new Date(+msepoch));
          ux.loading.getTimeGeo = false;
          ux.userLocation = { lat, long };
          ux.currentDate = currentDate;
          ux.clientMsOffset = Date.now() - Number(msepoch);
          ux.calendar = createCalendar(currentDate, CALENDAR_DAYS, WeekdayKey.MON);
        }),
    },
    store
  );

  return {
    runSideEffects: ({ envConfig }: BookingState) => {
      effectActions._setTimeGeoLoading();
      getTimeGeo(envConfig).then(({ payload: { msepoch, lat, long } }: Response<TimeGeo>) => {
        effectActions._setTimeGeoData({ msepoch, lat, long });
      });
    },
    when: () => false,
    runOn: [{ type: LifecycleEvent.INIT }],
    name: getTimeGeoEffect.name,
  };
};

export const createCalendarEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _createCalendar: (state: BookingState): BookingState =>
        produce(state, ({ ux }: BookingState) => {
          ux.calendar = createCalendar(ux.currentDate, CALENDAR_DAYS, WeekdayKey.MON);
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._createCalendar,
    when: (prev, next) => currentDateChanged(prev, next) && currentDateAvailable(next),
    name: createCalendarEffect.name,
  };
};

export const clearPastTravelDatesEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearPastTravelDates: (state: BookingState): BookingState =>
        produce(state, ({ selection: { travelDates } }: BookingState) => {
          travelDates.departureDate = undefined;
          travelDates.returnDate = undefined;
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._clearPastTravelDates,
    when: (prev, next) =>
      changed(prev, next, 'ux', 'currentDate') && next?.selection?.travelDates?.departureDate < next.ux.currentDate,
    name: clearPastTravelDatesEffect.name,
  };
};

export const timeGeoEffects: SideEffectGenerator<BookingState>[] = [
  getTimeGeoEffect,
  createCalendarEffect,
  clearPastTravelDatesEffect,
];
